import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import Grid from 'semantic-ui-react/dist/commonjs/collections/Grid';
import {
  getInterventionBookingDashboardStatistics
} from 'services/interventionBookingServices';
import styled from 'styled-components';
import { InterventionExternalCode } from 'ts-types/api.enums';
import {
  InterventionBookingStatisticsDto,
  InterventionCovidTestDto,
  InterventionDto,
  ReportRowDto
} from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import moment from 'moment';

const MainContentDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  height: 100%;

  .page-header.page-header-ps {
    padding-top: 0;
    padding-bottom: 0.875rem !important;
  }

  .page-header .title {
    padding-left: 0.287rem;
    font-size: 1.1rem !important;
    color: #444444;
  }

  .content-panel {
    padding: 1rem;

    .lastRefreshData {
      font-size: 0.95rem;
      color: #9a9a9a;
      font-style: oblique;
    }

    .errorMessage {
      padding-left: 2rem !important;
      color: darkred;
    }

    .ReactVirtualized__Table__Grid {
      border-bottom: none;

      .ReactVirtualized__Table__row.total-row {
        border-top: 1px solid #5a5a5a;
        font-weight: bold;
      }
    }

    .oldest-results-labels {
      margin-top: 3.5rem;
      margin-left: 2rem;
    }
    .oldest-results-data {
      margin-top: 3.5rem;
    }
    .icon {
      margin-bottom: 5px;
      padding-left: 10px
    }
  }
`;

const StyledPraxSegment = styled(PraxStyledSegment)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  padding: 0.5rem 1rem 0.5rem 0.5rem !important;
  margin: 0 !important;
`;

const StyledRow = styled(Grid.Row)`
  min-height: 200px !important;
  margin-bottom: 0.25rem;
`;

const StyledHeader = styled('div')`
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 14px;
`;

interface ReportWithTotals {
  inProcess: ReportRowDto[];
  resultAvailable: ReportRowDto[];
  resultVerified: ReportRowDto[];
  total: ReportRowDto[];
}

interface Props extends RouteComponentProps<any>,
    MessageSourceProps,
    AuthConsumerRenderProps {

}

interface State {
  loadingInProgress: boolean;

  interventions: InterventionDto[];

  inProcess: ReportRowDto[];
  resultAvailable: ReportRowDto[];
  resultVerified: ReportRowDto[];

  tableReport: ReportWithTotals;
  lastDataRefresh: string;
  errorMessages: Array<string>;
  oldestTestWithoutResult?: InterventionCovidTestDto;
  oldestTestWithResultNotVerified?: InterventionCovidTestDto;
}

class PatientCentralDashboard extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private reportUpdateInterval = 60000;
  private updateReportInterval: number | undefined;

  constructor(props: Props) {
    super(props);

    this.state = {
      loadingInProgress: true,
      interventions: [],
      inProcess: [],
      resultAvailable: [],
      resultVerified: [],
      tableReport: {
        inProcess: [],
        resultAvailable: [],
        resultVerified: [],
        total: []
      },
      lastDataRefresh: "",
      errorMessages: []
    };

    this.generateReport();

    this.updateReportInterval = setInterval(() => {
      this.generateReport();
    }, this.reportUpdateInterval);
  }

  componentWillUnmount(): void {

    if (this.updateReportInterval) {
      clearInterval(this.updateReportInterval);
    }

    this.cancelTokenSource.cancel('Operation canceled by user');
  }

  goToHome = () => {
    const {history} = this.props;
    history.push("/patient-central");
  };

  generateReport = async () => {

    this.setState({
      loadingInProgress: true
    });

    const date = moment().format('YYYY-MM-DD, hh:mm:ss a');

    try {
      const bookingStatisticsData = await getInterventionBookingDashboardStatistics(this.cancelTokenSource);

      this.setState({
        inProcess: bookingStatisticsData.inProcess,
        resultAvailable: bookingStatisticsData.resultAvailable,
        resultVerified: bookingStatisticsData.resultVerified,
        oldestTestWithoutResult: bookingStatisticsData.oldestTestWithoutResult,
        oldestTestWithResultNotVerified: bookingStatisticsData.oldestTestWithResultNotVerified,
        tableReport: this.createTableReport(bookingStatisticsData)
      });

      this.setState({
        lastDataRefresh: date
      });

      // clear errors
      this.setErrorMessage();

    } catch (ex) {
      this.handleError(ex);
    } finally {
      this.setState({
        loadingInProgress: false
      });
    }
  }

  handleError = (error: any) => {
    const {getMessage} = this.props;

    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
          }
        });
      }

      if (!this.state.errorMessages.length) {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {
      this.setState({
        errorMessages: []
      });
    }
  };


  createTableReport = (statistics: InterventionBookingStatisticsDto): ReportWithTotals => {

    const totalReducer = (prev: ReportRowDto, current: ReportRowDto, ix: number, array: ReportRowDto[]) => {
      prev.day = prev.day + current.day;
      prev.week = prev.week + current.week;
      prev.month = prev.month + current.month;
      return prev;
    };

    const initialTotalRow: ReportRowDto = {
      externalCode: "PCR_SALIVA_TEST",
      description: "total",
      descriptionEn: "total",
      descriptionFr: "total",
      descriptionIt: "total",
      day: 0,
      week: 0,
      month: 0
    };

    const getTotalRow = (report: ReportRowDto[]): ReportRowDto => {
      return report.reduce(totalReducer, {...initialTotalRow});
    }

    const inProcessTotal = getTotalRow(statistics.inProcess);
    const resultAvailableTotal = getTotalRow(statistics.resultAvailable);
    const resultVerifiedTotal = getTotalRow(statistics.resultVerified);
    const totalRow = getTotalRow([inProcessTotal, resultAvailableTotal, resultVerifiedTotal]);

    const addTotalRow = (report: ReportRowDto[], totalRow: ReportRowDto): ReportRowDto[] => {
      return [...report, totalRow];
    }

    const tableReport = {
      inProcess: addTotalRow(statistics.inProcess, inProcessTotal),
      resultAvailable: addTotalRow(statistics.resultAvailable, resultAvailableTotal),
      resultVerified: addTotalRow(statistics.resultVerified, resultVerifiedTotal),
      total: [totalRow]
    }
    return tableReport;
  }

  render(): React.ReactNode {
    const {loadingInProgress} = this.state;

    return (
        <MainContentDiv>
          <StyledPraxSegment>
            <Grid className="content-panel">
              {
                loadingInProgress
                    ? this.renderLoader()
                    : <React.Fragment>
                      {this.renderTestInformation()}
                    </React.Fragment>
              }
            </Grid>
          </StyledPraxSegment>
        </MainContentDiv>
    )
  }

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("patientCentral.loader.message")}
          </Loader>
        </Dimmer>
    );
  };

  renderTestInformation = (): JSX.Element => {

    const {inProcess, resultAvailable, resultVerified, tableReport, lastDataRefresh, errorMessages,
      oldestTestWithoutResult, oldestTestWithResultNotVerified} = this.state;
    const {getMessage, getMessageWithNamedParams} = this.props;

    let oldestTestWithoutResultDate;
    let oldestTestWithoutResultTestKitCode;
    let oldestTestWithResultNotVerifiedDate;
    let oldestTestWithResultNotVerifiedTestKitCode;

    if (oldestTestWithoutResult) {
      oldestTestWithoutResultDate = moment(oldestTestWithoutResult.registrationTimestamp).format("YYYY-MM-DD");

      oldestTestWithoutResult.testKitCode
          ? oldestTestWithoutResultTestKitCode = oldestTestWithoutResult.testKitCode
          : oldestTestWithoutResultTestKitCode = getMessage("enum.InterventionExternalCode.RAPID_SALIVA_TEST")
    } else {
      oldestTestWithoutResultDate = getMessage("enum.interventionBookingAuthorityNotificationStatus.NA");
      oldestTestWithoutResultTestKitCode = getMessage("enum.interventionBookingAuthorityNotificationStatus.NA");
    }

    if (oldestTestWithResultNotVerified) {
      oldestTestWithResultNotVerifiedDate =
          moment(oldestTestWithResultNotVerified.registrationTimestamp).format("YYYY-MM-DD");

      oldestTestWithResultNotVerified.testKitCode
          ? oldestTestWithResultNotVerifiedTestKitCode = oldestTestWithResultNotVerified.testKitCode
          : oldestTestWithResultNotVerifiedTestKitCode =
              getMessage("enum.InterventionExternalCode.RAPID_SALIVA_TEST")
    } else {
      oldestTestWithResultNotVerifiedDate =
          getMessage("enum.interventionBookingAuthorityNotificationStatus.NA");
      oldestTestWithResultNotVerifiedTestKitCode =
          getMessage("enum.interventionBookingAuthorityNotificationStatus.NA");
    }

    return (
        <React.Fragment>
          <span className="lastRefreshData">
            {getMessageWithNamedParams("patientCentral.dashboard.lastDataRefresh",
                {reportUpdateInterval: lastDataRefresh})}
          </span>
          {
            errorMessages.length > 0 &&
            <span className="errorMessage">
                <Icon name="exclamation triangle" color="red" style={{paddingRight: "1rem"}}/> {errorMessages.map(err =>
                <span key={err}>{err}</span>)}
            </span>
          }
          <StyledRow>
            <Grid.Column width={6}>
              {this.renderTableContent(
                  tableReport.inProcess, "patientCentral.dashboard.beforeResults.table.header")}
            </Grid.Column>
            <Grid.Column width={10}>
              {this.renderGraphContent(inProcess)}
            </Grid.Column>
          </StyledRow>
          <StyledRow>
            <Grid.Column width={6}>
              {this.renderTableContent(
                  tableReport.resultAvailable, "patientCentral.dashboard.beforeValidation.table.header")}
            </Grid.Column>
            <Grid.Column width={10}>
              {this.renderGraphContent(resultAvailable)}
            </Grid.Column>
          </StyledRow>
          <StyledRow>
            <Grid.Column width={6}>
              {this.renderTableContent(
                  tableReport.resultVerified, "patientCentral.dashboard.afterValidation.table.header")}
            </Grid.Column>
            <Grid.Column width={10}>
              {this.renderGraphContent(resultVerified)}
            </Grid.Column>
          </StyledRow>
          <StyledRow>
            <Grid.Column width={6}>
              {this.renderTableContent(
                  tableReport.total, "common.total")}
            </Grid.Column>
            <Grid.Column width={3}>
              <div className="oldest-results-labels">
                <label>{getMessage("dashboard.oldestTestWithoutResult.label")}</label>
                <br/>
                <label>{getMessage("dashboard.oldestTestWitResultNotVerified.label")}</label>
              </div>
            </Grid.Column>
            <Grid.Column width={7}>
              <div className="oldest-results-data">
                <span>{getMessageWithNamedParams("dashboard.oldestTestWithoutResult.data", {
                  testDate: oldestTestWithoutResultDate, testKitCode: oldestTestWithoutResultTestKitCode})}</span>
                <br/>
                <span>{getMessageWithNamedParams("dashboard.oldestTestWithoutResult.data", {
                  testDate: oldestTestWithResultNotVerifiedDate, testKitCode: oldestTestWithResultNotVerifiedTestKitCode})}</span>
              </div>
            </Grid.Column>
          </StyledRow>
        </React.Fragment>
    )
  }

  renderTableContent = (report: ReportRowDto[], tableHeader: string): JSX.Element => {
    const {getMessage} = this.props;

    return (
        <>
          <StyledHeader>{getMessage(tableHeader)}</StyledHeader>
          <VirtualizedTable
              rowCount={report.length}
              rowGetter={this.testResultsRowGetter(report)}
              rowRenderer={this.testResultsRowRenderer}
              headerHeight={37}
              rowHeight={37}
              columns={[
                {
                  width: 100,
                  label: "",
                  dataKey: "description",
                  cellRenderer: this.testTypeDescriptionCellRenderer,
                  flexGrow: 1
                },
                {
                  width: 100,
                  label: getMessage("patientCentral.dashboard.table.dailyColumn.header"),
                  dataKey: "day"
                },
                {
                  width: 100,
                  label: getMessage("patientCentral.dashboard.table.weeklyColumn.header"),
                  dataKey: "week"
                },
                {
                  width: 100,
                  label: getMessage("patientCentral.dashboard.table.monthlyColumn.header"),
                  dataKey: "month"
                }
              ]}/>
        </>
    );
  }

  testResultsRowGetter = (testResults: any) => ({index}: any) => {

    Object.assign(testResults[index], {index: index + 1});

    return testResults[index];
  };

  testResultsRowRenderer = ({className, columns, index, key, style, rowData}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const testsRowName = <>
      {rowData.externalCode === InterventionExternalCode.PCR_SALIVA_TEST
          ? <Icon name="square" color="blue" className="icon"/>
          : <Icon name="square" color="green" className="icon"/>
      }
    </>
    const totalRowName = <>
      <Icon name="square" className="icon"/>
    </>
    return (
        <div
            {...a11yProps}
            className={rowData.description === "total" ? `${className} total-row` : className}
            key={key}
            role="row"
            style={style}
        >
          {rowData.description !== 'total'
              ? testsRowName
              : totalRowName
          } {columns}
        </div>
    );
  };

  testTypeDescriptionCellRenderer = ({rowData}: any) => {

    if (rowData.description === "total") {
      return this.props.getMessage("common.total");
    }

    const texts: { [key: string]: string } = {
      "de": rowData.description,
      "en": rowData.descriptionEn,
      "fr": rowData.descriptionFr,
      "it": rowData.descriptionIt
    };

    return texts[this.props.language] || rowData.description;
  };

  renderGraphContent = (testResults: ReportRowDto[]): JSX.Element => {
    const graphColors = ["#2185d0", "#21ba45"];
    const renderEmptyGraph = (cx: number, cy: number) => {
      const emptyGraph = [{value: 1}, {value: 0}];
      return (
          <Pie
              data={emptyGraph}
              cx={cx}
              cy={cy}
              innerRadius={40}
              outerRadius={55}
              fill="#a1a1a1"
              dataKey="value"
          />
      )
    }

    return (
        <ResponsiveContainer>

          <PieChart>
            {testResults.length === 0 || (testResults[0].day === 0 && testResults[1].day === 0)
                ? renderEmptyGraph(100, 90)
                : <Pie
                    data={testResults}
                    cx={100}
                    cy={90}
                    innerRadius={40}
                    outerRadius={55}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="day"
                    label
                >
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                </Pie>
            }
            {testResults.length === 0 || (testResults[0].week === 0 && testResults[1].week === 0)
                ? renderEmptyGraph(324, 90)
                : <Pie
                    data={testResults}
                    cx={324}
                    cy={90}
                    innerRadius={40}
                    outerRadius={55}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="week"
                    label
                >
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                </Pie>
            }
            {testResults.length === 0 || (testResults[0].month === 0 && testResults[1].month === 0)
                ? renderEmptyGraph(548, 90)
                : <Pie
                    data={testResults}
                    cx={548}
                    cy={90}
                    innerRadius={40}
                    outerRadius={55}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="month"
                    label
                >
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                  {testResults.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={graphColors[index % graphColors.length]}/>
                  ))}
                </Pie>
            }
          </PieChart>
        </ResponsiveContainer>
    );
  }

}

export default withRouterWorkaround(
    withAuthContext(
        withMessages(
            PatientCentralDashboard)));
